// PROMPT 3.3M — Community Loadouts v2 (Defect H).
// Alt-text is generated at render time from the image filename + id.
// Section title: "OPERATOR LOADOUTS / FROM OUR COMMUNITY."
// Grid: 4/4/3/2/1 across 2xl/xl/lg/md/sm.
// Modal: centered on lg+, full-bleed top sheet on sm/md with swipe-down.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HOME } from "../constants/testIds";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// ---- Alt-text generation ---------------------------------------------------
// Six patterns per PROMPT 3.3 spec, suffixed with the shared trust tag.
const ALT_SUFFIX = "— submitted by an Indian esports operator";

function generateAlt(image) {
        const filename = (image.filename || "").toLowerCase();
        // 1) IMG_XXXX pattern → "Setup shot IMG-XXXX"
        let m = filename.match(/img[-_]?(\d{3,})/);
        if (m) return `Setup shot IMG-${m[1]} ${ALT_SUFFIX}`;
        // 2) whatsapp
        m = filename.match(/(?:whatsapp|wa)[-_ ]?image[-_ ]?(\d{4}-?\d{2}-?\d{2})/);
        if (m) return `WhatsApp submission ${m[1]} ${ALT_SUFFIX}`;
        // 3) screenshot
        m = filename.match(/screenshot[-_ ]?(\d{4}-?\d{2}-?\d{2}|\d{6,})/);
        if (m) return `Screenshot ${m[1]} ${ALT_SUFFIX}`;
        // 4) pxl (Pixel phone)
        m = filename.match(/pxl[-_ ]?(\d{6,})/);
        if (m) return `Pixel capture PXL-${m[1]} ${ALT_SUFFIX}`;
        // 5) rn_image_picker (React-Native app export)
        if (filename.includes("rn_image_picker") || filename.includes("rn-image-picker")) {
                return `Community upload ${ALT_SUFFIX}`;
        }
        // 6) Fallback — trim id tail
        const idTail = (image.id || "").slice(-6);
        const s = `Operator loadout ${idTail} ${ALT_SUFFIX}`;
        return s.length > 140 ? s.slice(0, 137) + "…" : s;
}

// ---- Lightbox --------------------------------------------------------------
function Lightbox({ image, altOf, onClose }) {
        const startY = useRef(null);
        const currentY = useRef(null);
        const sheetRef = useRef(null);

        useEffect(() => {
                const onKey = (e) => { if (e.key === "Escape") onClose(); };
                document.addEventListener("keydown", onKey);
                return () => document.removeEventListener("keydown", onKey);
        }, [onClose]);

        // Swipe-down close on touch — only when the touch starts in the top 30 %
        // of the sheet and the delta exceeds 80 px.
        const onTouchStart = (e) => {
                const y = e.touches[0].clientY;
                const rect = sheetRef.current?.getBoundingClientRect();
                if (!rect) return;
                if (y - rect.top < rect.height * 0.3) startY.current = y;
        };
        const onTouchMove = (e) => {
                if (startY.current == null) return;
                currentY.current = e.touches[0].clientY;
        };
        const onTouchEnd = () => {
                if (startY.current != null && currentY.current != null) {
                        if (currentY.current - startY.current >= 80) onClose();
                }
                startY.current = null;
                currentY.current = null;
        };

        if (!image) return null;
        const altText = altOf(image);
        return (
                <div
                        role="dialog"
                        aria-modal="true"
                        data-testid={HOME.communityLightbox}
                        className="fixed inset-0 z-50 bg-black/90 flex items-start md:items-center justify-center"
                        onClick={onClose}
                >
                        <div
                                ref={sheetRef}
                                onClick={(e) => e.stopPropagation()}
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                                className="relative w-full md:max-w-[1100px] md:max-h-[80dvh] h-[100dvh] md:h-auto md:mx-4 bg-[#0A0A0A] md:bg-transparent flex flex-col md:grid md:grid-cols-[1fr_320px] md:gap-6"
                                style={{ paddingTop: "env(safe-area-inset-top)" }}
                        >
                                <button
                                        type="button"
                                        aria-label="Close"
                                        onClick={onClose}
                                        className="touch-target absolute top-2 right-2 md:top-4 md:right-4 z-10 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white font-mono text-[22px] leading-none"
                                >
                                        ✕
                                </button>
                                <img
                                        src={image.src}
                                        alt={altText}
                                        className="w-full max-h-[70dvh] md:max-h-[80dvh] object-contain"
                                />
                                <div className="text-white p-6 flex flex-col justify-center">
                                        <p className="font-mono text-[11px] tracking-[0.14em] text-[#FF9F1C] uppercase">
                                                Community submission
                                        </p>
                                        <p className="mt-3 font-sans text-[14px] leading-relaxed text-white/85">
                                                {altText}
                                        </p>
                                </div>
                        </div>
                </div>
        );
}

// ---- Main section ----------------------------------------------------------
// PROMPT 3.5c — Horizontal infinite scroll (never expands page height).
// Users can scroll past the section vertically OR pan horizontally through
// the whole set. Batch size stays small so we always have more to load.
const RESPONSIVE_BATCH = { sm: 4, md: 6, lg: 8 };

export default function CommunityLoadouts() {
        const [images, setImages] = useState([]);
        const [cursor, setCursor] = useState(0);
        const [nextCursor, setNextCursor] = useState("0");
        const [status, setStatus] = useState("loading");
        const [selected, setSelected] = useState(null);
        const scrollerRef = useRef(null);
        const sentinelRef = useRef(null);

        // Compute batch size from viewport width.
        const batchSize = useMemo(() => {
                if (typeof window === "undefined") return 8;
                const w = window.innerWidth;
                if (w <= 480) return RESPONSIVE_BATCH.sm;
                if (w <= 768) return RESPONSIVE_BATCH.md;
                return RESPONSIVE_BATCH.lg;
        }, []);

        const altOf = useCallback((img) => generateAlt(img), []);

        const load = useCallback(async (from) => {
                try {
                        const r = await fetch(`${BACKEND}/api/community/loadouts?cursor=${from}&limit=${batchSize}`);
                        if (!r.ok) { setStatus("error"); return; }
                        const j = await r.json();
                        const withAbs = (arr) => (arr || []).map((x) => ({
                                ...x,
                                // Backend-served /api/community/images/{id} URLs are relative — prefix with backend origin.
                                src: (x.src || "").startsWith("http") ? x.src : `${BACKEND}${x.src}`,
                        }));
                        setImages((prev) => {
                                if (from === 0) return withAbs(j.images);
                                const seen = new Set(prev.map((x) => x.id));
                                const add = withAbs(j.images).filter((x) => !seen.has(x.id));
                                return prev.concat(add);
                        });
                        setNextCursor(j.nextCursor || null);
                        setStatus("ready");
                } catch (_e) {
                        setStatus("error");
                }
        }, [batchSize]);

        useEffect(() => { load(0); }, [load]);

        // Horizontal IntersectionObserver — root is the scroll container so the
        // sentinel fires when it enters the viewport from the RIGHT edge as the
        // user pans through the row.
        useEffect(() => {
                if (!nextCursor) return;
                const el = sentinelRef.current;
                const root = scrollerRef.current;
                if (!el || !root) return;
                const io = new IntersectionObserver((entries) => {
                        for (const e of entries) {
                                if (e.isIntersecting) {
                                        const nc = parseInt(nextCursor, 10);
                                        if (!Number.isNaN(nc) && nc !== cursor) {
                                                setCursor(nc);
                                                load(nc);
                                        }
                                }
                        }
                }, { root, rootMargin: "0px 400px 0px 0px" });
                io.observe(el);
                return () => io.disconnect();
        }, [nextCursor, cursor, load]);

        // Nudge buttons (desktop only) — scroll one card-width.
        const nudge = useCallback((dir) => {
                const el = scrollerRef.current;
                if (!el) return;
                const first = el.querySelector("[data-community-tile]");
                const step = first ? first.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
                el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
        }, []);

        // PROMPT 3.5 — ALWAYS render the container (never return null).
        // A zero-tile placeholder replaces the grid so the section is visible.

        return (
                <section
                        data-testid={HOME.communitySection}
                        className="bg-[#0A0A0A] text-white py-14 md:py-24 border-t border-white/10"
                >
                        <div className="mx-auto max-w-[1440px] px-6" data-testid="community-strip-container">
                                <p
                                        data-testid={HOME.communityEyebrow}
                                        className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C]"
                                >
                                        03 / Operatives Worldwide
                                </p>
                                <h2
                                        data-testid={HOME.communityHeadline}
                                        className="mt-3 font-display leading-[0.95] uppercase community-heading"
                                >
                                        <span className="block text-white">OPERATOR LOADOUTS</span>
                                        <span className="block text-[#FF9F1C]">FROM OUR COMMUNITY.</span>
                                </h2>
                                <p
                                        data-testid={HOME.communitySubhead}
                                        className="mt-6 font-sans text-white/80 leading-relaxed community-subhead"
                                        style={{ maxWidth: "56ch" }}
                                >
                                        3,200+ setups shipped from Indian desks. Every photo below comes from a
                                        real purchase — a real setup that's gaming every day. No stock photos.
                                        No paid placements.
                                </p>

                                {/* Operator-facing count line (PROMPT 3.5 — object-storage backed) */}
                                <p
                                        data-testid="community-count-line"
                                        className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-white/50"
                                >
                                        {images.length > 0
                                                ? `Showing ${images.length} community loadouts.`
                                                : `Gallery is empty. Sign in at /admin/community and upload an image to publish.`}
                                </p>

                                {status === "loading" && (
                                        <p
                                                data-testid={HOME.communityLoading}
                                                className="mt-10 font-mono text-[11px] tracking-[0.18em] uppercase text-white/40"
                                        >
                                                Loading…
                                        </p>
                                )}

                                {status !== "loading" && images.length === 0 && (
                                        <div
                                                data-testid="community-zero-placeholder"
                                                className="mt-8 py-16 border border-dashed border-white/15 flex items-center justify-center"
                                        >
                                                <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/40 text-center max-w-md px-6">
                                                        OPERATOR LOADOUTS arrive here soon — the gallery is empty.
                                                        Sign in at /admin/community to upload the first setup.
                                                </p>
                                        </div>
                                )}

                                {images.length > 0 && (
                                        <div className="mt-10 relative community-rail-wrap">
                                                {/* Nudge buttons — desktop only. */}
                                                <button
                                                        type="button"
                                                        aria-label="Scroll left"
                                                        data-testid="community-nudge-left"
                                                        onClick={() => nudge(-1)}
                                                        className="community-nudge community-nudge-left"
                                                >
                                                        ‹
                                                </button>
                                                <button
                                                        type="button"
                                                        aria-label="Scroll right"
                                                        data-testid="community-nudge-right"
                                                        onClick={() => nudge(1)}
                                                        className="community-nudge community-nudge-right"
                                                >
                                                        ›
                                                </button>

                                                <div
                                                        ref={scrollerRef}
                                                        data-testid={HOME.communityGrid}
                                                        className="community-rail"
                                                >
                                                        {images.map((img) => {
                                                                const altText = altOf(img);
                                                                return (
                                                                        <button
                                                                                key={img.id}
                                                                                type="button"
                                                                                data-testid={HOME.communityCard}
                                                                                data-community-tile
                                                                                onClick={() => setSelected(img)}
                                                                                className="community-tile tile-anim group text-left border border-white/10 overflow-hidden hover:border-[#FF9F1C] transition-colors"
                                                                        >
                                                                                <div
                                                                                        className="bg-[#111] overflow-hidden"
                                                                                        style={{ aspectRatio: "1 / 1" }}
                                                                                >
                                                                                        <img
                                                                                                src={img.src}
                                                                                                alt={altText}
                                                                                                loading="lazy"
                                                                                                decoding="async"
                                                                                                width="600"
                                                                                                height="600"
                                                                                                className="w-full h-full object-cover"
                                                                                                style={{ objectPosition: "center top" }}
                                                                                        />
                                                                                </div>
                                                                        </button>
                                                                );
                                                        })}
                                                        {nextCursor && (
                                                                <div
                                                                        ref={sentinelRef}
                                                                        data-testid={HOME.communitySentinel}
                                                                        className="community-sentinel"
                                                                        aria-hidden="true"
                                                                />
                                                        )}
                                                </div>

                                                <p
                                                        data-testid="community-scroll-hint"
                                                        className="mt-4 font-mono text-[10px] tracking-[0.16em] uppercase text-white/30"
                                                >
                                                        ← Swipe / scroll to see more →
                                                </p>
                                        </div>
                                )}
                        </div>
                        <Lightbox image={selected} altOf={altOf} onClose={() => setSelected(null)} />
                        <style>{`
                                .community-heading { font-size: 36px; }
                                .community-subhead { font-size: 15px; }
                                @media (min-width: 481px) { .community-heading { font-size: 40px; } .community-subhead { font-size: 16px; } }
                                @media (min-width: 769px) { .community-heading { font-size: 48px; } }
                                @media (min-width: 1024px) { .community-heading { font-size: 56px; } }
                                @media (min-width: 1280px) { .community-heading { font-size: 64px; } }

                                /* Horizontal scrolling rail (PROMPT 3.5c). Users can drag / swipe /
                                   scroll horizontally, and the page continues below normally. */
                                .community-rail-wrap { position: relative; }
                                .community-rail {
                                        display: flex;
                                        gap: 16px;
                                        overflow-x: auto;
                                        overflow-y: hidden;
                                        scroll-snap-type: x mandatory;
                                        -webkit-overflow-scrolling: touch;
                                        overscroll-behavior-x: contain;
                                        padding-bottom: 8px;
                                        /* Fade edges so tiles feel like they scroll beyond the container. */
                                        mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
                                        -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
                                }
                                .community-rail::-webkit-scrollbar { height: 6px; }
                                .community-rail::-webkit-scrollbar-track { background: transparent; }
                                .community-rail::-webkit-scrollbar-thumb {
                                        background: rgba(255,255,255,0.14);
                                        border-radius: 3px;
                                }
                                .community-rail::-webkit-scrollbar-thumb:hover { background: #FF9F1C; }

                                .community-tile {
                                        flex: 0 0 auto;
                                        width: 260px;
                                        scroll-snap-align: start;
                                }
                                @media (min-width: 481px) { .community-tile { width: 280px; } }
                                @media (min-width: 769px) { .community-tile { width: 320px; } }
                                @media (min-width: 1024px) { .community-tile { width: 360px; } }
                                @media (min-width: 1280px) { .community-tile { width: 380px; } }

                                .community-sentinel {
                                        flex: 0 0 auto;
                                        width: 1px;
                                        height: 1px;
                                        align-self: center;
                                }

                                /* Nudge buttons — desktop only. Hidden on touch (no hover). */
                                .community-nudge {
                                        position: absolute;
                                        top: 50%;
                                        transform: translateY(-50%);
                                        z-index: 5;
                                        width: 44px;
                                        height: 44px;
                                        display: none;
                                        align-items: center;
                                        justify-content: center;
                                        background: rgba(10,10,10,0.85);
                                        color: white;
                                        font-size: 28px;
                                        line-height: 1;
                                        border: 1px solid rgba(255,255,255,0.14);
                                        cursor: pointer;
                                        transition: border-color 200ms ease, background 200ms ease;
                                }
                                .community-nudge:hover { border-color: #FF9F1C; background: rgba(255,159,28,0.15); }
                                .community-nudge-left { left: -22px; }
                                .community-nudge-right { right: -22px; }
                                @media (hover: hover) and (pointer: fine) {
                                        .community-nudge { display: flex; }
                                }

                                @media (hover: hover) and (pointer: fine) {
                                        .tile-anim:hover img { transform: scale(1.02); border-color: #FF9F1C; }
                                        .tile-anim { transition: border-color 200ms ease; }
                                        .tile-anim img { transition: transform 300ms ease; }
                                }
                                @media (prefers-reduced-motion: reduce) {
                                        .tile-anim, .tile-anim img { transition: none !important; transform: none !important; }
                                        .community-rail { scroll-behavior: auto; }
                                }
                        `}</style>
                </section>
        );
}
